import { useRef, useState } from "react";
import { Trans } from '@lingui/macro';
import { Loader2, Check } from "lucide-react";
import { Room } from "../../models/Room";
import { User, UserSimplified } from "../../models/User";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Util } from "../../services/Utils/Util";

interface UserFindProps {
  onSelectedUser: (user: User) => void,
  header: string,
  placeholder: string,
  room: Room | undefined
}

const UserFind: React.FC<UserFindProps> = ({ onSelectedUser, header, placeholder, room }) => {
  const MIN_CHARS = 3;
  const usernameInput = useRef<HTMLInputElement>(null);
  const [searchValue, setFindUsername] = useState("");
  const [inProgress, setInProgress] = useState(false);
  const [allUsers, setAllUsers] = useState<UserSimplified[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserSimplified[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isCreator = room?.creatorId && room.creatorId == My.user?.id;

  async function userSelected(user: UserSimplified) {
    setFindUsername("")
    setFilteredUsers([]);
    setSelectedIndex(0);
    Fire.getUser(user.id).then(user => {
      if (user)
        onSelectedUser(user);
    });
  }

  function inputChanged(value: string | null | undefined) {
    if (!value || value.length < MIN_CHARS) {
      // Reset to search again.
      setAllUsers([]);
      setFilteredUsers([]);
    } else if (value?.length === MIN_CHARS && allUsers.length === 0 && !inProgress) {
      setInProgress(true);
      Fire.searchForUsers(value).then(list => {
        setAllUsers(list);
        setFilteredUsers(list);
        setInProgress(false);
      });
    } else if (value && value.length >= MIN_CHARS) {
      let foundList = allUsers.filter(user => user.name.toLowerCase().startsWith(value.toLowerCase()));
      setFilteredUsers(foundList);
    }
    setFindUsername(value ?? "");
  }

  function selectSuggestion(index: number) {
    if (index >= 0 && index < filteredUsers.length) {
      userSelected(filteredUsers[index])
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      //down
      e.preventDefault();
      setSelectedIndex(prev => prev < filteredUsers.length ? ++prev : prev);
    } else if (e.key === 'ArrowUp') {
      //up
      e.preventDefault();
      setSelectedIndex(prev => prev === -1 ? prev : --prev);
    } else if (e.key === 'Enter') {
      //enter
      e.preventDefault();
      if (selectedIndex > -1) {
        selectSuggestion(selectedIndex);
      }
    }
    // else {
    //   inputChanged(findUsername);
    // }
  }

  if (!isCreator) return null;

  return (
    <div className="flex flex-col gap-2 w-full">
      <Input
        ref={usernameInput}
        autoFocus={true}
        value={searchValue}
        onChange={e => inputChanged(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={50}
        data-cy="search"
        className="w-full"
      />

      {filteredUsers.length > 0 && (
        <div className="user-list border rounded-md bg-white shadow-sm mt-1 overflow-hidden">
          <h6 className="text-xs font-semibold text-green-600 px-3 py-2 bg-gray-50 uppercase tracking-wide">
            <Trans>Select a user</Trans>
          </h6>
          <div className="divide-y max-h-60 overflow-y-auto">
            {filteredUsers.map((u, index) => {
              const isSelected = index === selectedIndex;
              return (
                <div
                  key={u.id}
                  onClick={() => userSelected(u)}
                  className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  data-cy="found-user"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={u.photoUrl || Util.ANONYMOUS_PHOTO_URL} />
                      <AvatarFallback>{u.name.substring(0, 1)}</AvatarFallback>
                    </Avatar>
                    <p className="text-sm font-medium">{u.name}</p>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-primary" />}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {inProgress && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
        </div>
      )}

      {!inProgress && searchValue.length >= MIN_CHARS && filteredUsers.length === 0 && (
        <div className="p-3 text-sm text-gray-500 text-center border rounded-md bg-gray-50">
          <Trans>No user found</Trans>
        </div>
      )}
    </div>
  );
}

export default UserFind;
